import TextFieldsIcon from "@mui/icons-material/TextFields";
import TitleIcon from "@mui/icons-material/Title";
import SmartButtonIcon from "@mui/icons-material/SmartButton";
import LinkIcon from "@mui/icons-material/Link";
import ImageIcon from "@mui/icons-material/Image";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import HeightIcon from "@mui/icons-material/Height";
import MenuIcon from "@mui/icons-material/Menu";
import ShareIcon from "@mui/icons-material/Share";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import WallpaperIcon from "@mui/icons-material/Wallpaper";
import VerticalSplitIcon from "@mui/icons-material/VerticalSplit";
import CodeIcon from "@mui/icons-material/Code";

const ALIGN = {
  key: "align",
  label: "Alignment",
  type: "align",
};

const SEP_CHARS = { none: "", pipe: "|", slash: "/", dot: "&#8226;" };

const FONT_OPTIONS = [
  { value: "", label: "Inherit (default)" },
  { value: "'Poppins', Arial, Helvetica, sans-serif", label: "Poppins" },
  { value: "Arial, Helvetica, sans-serif", label: "Arial" },
  { value: "'Helvetica Neue', Helvetica, Arial, sans-serif", label: "Helvetica" },
  { value: "Georgia, 'Times New Roman', serif", label: "Georgia" },
  { value: "'Times New Roman', Times, serif", label: "Times New Roman" },
  { value: "Tahoma, Geneva, sans-serif", label: "Tahoma" },
  { value: "Verdana, Geneva, sans-serif", label: "Verdana" },
  { value: "'Courier New', Courier, monospace", label: "Courier New" },
];

const FONT_FIELD = {
  key: "fontFamily",
  label: "Font",
  type: "select",
  options: FONT_OPTIONS,
};

const fontOf = (p, s) => p.fontFamily || s.fontFamily;

const escAttr = (v = "") => String(v).replace(/"/g, "&quot;");

const wrap = (inner, { align = "left", py = 10, px = 24, bg = "" } = {}) =>
  `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"${
    bg ? ` bgcolor="${bg}"` : ""
  }><tr><td align="${align}" style="padding:${py}px ${px}px;${
    bg ? `background-color:${bg};` : ""
  }">${inner}</td></tr></table>`;

// Paints a solid color as a linear-gradient too, which Gmail dark mode does
// not recolor - keeps section backgrounds stable across light/dark.
const bgLock = (color) =>
  color
    ? `background-color:${color};background-image:linear-gradient(${color},${color});`
    : "";

// Renders a single email-safe button (filled or outlined).
const btnHtml = ({
  label,
  href,
  bg = "#069d6e",
  color = "#ffffff",
  radius = 6,
  outline = false,
  ff = "Arial, Helvetica, sans-serif",
  size = "md",
}) => {
  if (!label) return "";
  const pad = size === "sm" ? "8px 18px" : "12px 26px";
  const fontSize = size === "sm" ? 13 : 15;
  const border = outline ? `2px solid ${bg}` : "0";
  const fill = outline ? "transparent" : bg;
  const textColor = outline ? bg : color;
  return `<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="display:inline-block;vertical-align:middle;margin:6px 6px;"><tr><td bgcolor="${
    outline ? "" : bg
  }" style="border-radius:${radius}px;${
    outline ? "" : `background-color:${bg};`
  }"><a href="${escAttr(
    href || "#"
  )}" target="_blank" style="display:inline-block;padding:${pad};font-family:${ff};font-size:${fontSize}px;font-weight:bold;color:${textColor};text-decoration:none;border-radius:${radius}px;border:${border};background:${fill};">${label}</a></td></tr></table>`;
};

const TAP_ASSET = (file) =>
  `https://authapi.tapinvest.com/storage/v1/object/public/web-app/app/${file}`;

const footerDef = (theme) => ({
  label: theme === "dark" ? "Footer (Dark)" : "Footer (Light)",
  icon: ContactMailIcon,
  defaultProps: {
    theme,
    fontFamily: "Poppins, sans-serif",
    logoSrc: theme === "dark" ? TAP_ASSET("logo-dark.png") : TAP_ASSET("logo.png"),
    logoHref: "https://www.tapinvest.com",
    logoWidth: 120,
    logoHeight: 34,
    socialLinks: [
      { href: "https://x.com/tapinvest", iconSrc: TAP_ASSET("x1.png"), alt: "twitter" },
      {
        href: "https://www.instagram.com/thetapwallet",
        iconSrc: TAP_ASSET("instagram2.png"),
        alt: "instagram",
      },
      {
        href: "https://www.linkedin.com/company/thetapwallet",
        iconSrc: TAP_ASSET("linkedin1.png"),
        alt: "linkedin",
      },
      {
        href: "https://www.tiktok.com/@thetapwallet",
        iconSrc: TAP_ASSET("tiktok1.png"),
        alt: "tiktok",
      },
    ],
    disclaimer:
      "Advisory services offered by SocialTrader.ai Inc, an SEC-registered investment adviser. TAP Invest is a product name used by SocialTrader.ai Inc. Registration denotes regulatory status and should not be interpreted as assurance of skill, training, or performance.",
    links: [
      { label: "Privacy Policy", href: "https://tapinvest.com/privacy-policy/" },
      { label: "Terms of Service", href: "https://tapinvest.com/terms-of-service/" },
      { label: "Contact us", href: "mailto:support@tapinvest.com" },
      { label: "Disclosures", href: "https://tapinvest.com/legal-disclosures/" },
      { label: "Unsubscribe", href: "https://www.socialtrader.ai/unsubscribe" },
    ],
    copyright: "TAP, Inc., 2025 All Rights Reserved",
    paddingY: 16,
  },
  fields: [
    {
      key: "theme",
      label: "Theme",
      type: "select",
      options: [
        { value: "light", label: "Light" },
        { value: "dark", label: "Dark" },
      ],
    },
    { key: "fontFamily", label: "Font family", type: "text" },
    { key: "logoSrc", label: "Logo image URL", type: "text" },
    { key: "logoHref", label: "Logo link URL", type: "text" },
    { key: "logoWidth", label: "Logo width (px)", type: "number" },
    { key: "logoHeight", label: "Logo height (px)", type: "number" },
    {
      key: "socialLinks",
      label: "Social icons",
      type: "items",
      itemFields: [
        { key: "alt", label: "Name" },
        { key: "iconSrc", label: "Icon image URL" },
        { key: "href", label: "Link URL" },
      ],
      newItem: { alt: "social", iconSrc: "", href: "#" },
    },
    { key: "disclaimer", label: "Disclaimer text", type: "textarea" },
    {
      key: "links",
      label: "Footer links",
      type: "items",
      itemFields: [
        { key: "label", label: "Label" },
        { key: "href", label: "URL" },
      ],
      newItem: { label: "Link", href: "#" },
    },
    { key: "copyright", label: "Copyright text", type: "text" },
    { key: "paddingY", label: "Padding top/bottom", type: "number" },
  ],
  toHtml: (p) => {
    const dark = p.theme === "dark";
    // Off-pure colors: Gmail/Outlook dark mode invert pure #000/#fff most
    // aggressively, so we nudge them slightly to reduce harsh recoloring.
    const bg = dark ? "#0a0a0a" : "#fefefe";
    const textColor = dark ? "#fafafa" : "#0a0a0a";
    const copyrightColor = dark ? "#888888" : "#c6cad2";
    const ff = p.fontFamily || "Poppins, sans-serif";

    const logoImg = `<img src="${escAttr(p.logoSrc)}" alt="logo" height="${
      p.logoHeight || 34
    }" width="${
      p.logoWidth || 120
    }" style="object-fit:contain;object-position:left;border:0;display:block;" />`;
    const logo = p.logoHref
      ? `<a href="${escAttr(p.logoHref)}" style="text-decoration:none;">${logoImg}</a>`
      : logoImg;

    const icons = (p.socialLinks || [])
      .map(
        (it) =>
          `<div style="width:25px;height:25px;border-radius:50%;text-align:center;vertical-align:middle;display:inline-block;"><a href="${escAttr(
            it.href
          )}" style="text-decoration:none;"><img src="${escAttr(
            it.iconSrc
          )}" alt="${escAttr(
            it.alt || ""
          )}" style="margin-top:6px;width:20px;height:20px;" /></a></div>`
      )
      .join("");

    const header = `<div style="margin-bottom:40px;"><table style="width:100%;"><tbody><tr><td><div>${logo}</div></td><td style="text-align:right;"><div style="display:inline-block;vertical-align:top;">${icons}</div></td></tr></tbody></table></div>`;

    const disclaimer = p.disclaimer
      ? `<div style="font-size:14px;line-height:20px;margin-bottom:24px;font-weight:500;font-family:${ff};color:${textColor};">${p.disclaimer}</div>`
      : "";

    const linkItems = (p.links || [])
      .map(
        (it) =>
          `<div style="display:inline-block;margin-right:10px;"><a href="${escAttr(
            it.href
          )}" style="text-decoration:none;font-size:14px;font-weight:500;font-family:${ff};color:${textColor};">${
            it.label || ""
          }</a></div>`
      )
      .join("");
    const linkRow = linkItems
      ? `<div><table style="width:100%;"><tbody><tr><td><div>${linkItems}</div></td></tr></tbody></table></div>`
      : "";

    const copyright = p.copyright
      ? `<div style="color:${copyrightColor};font-size:12px;margin-top:10px;font-family:${ff};">${p.copyright}</div>`
      : "";

    // Lock the background against Gmail dark-mode recoloring: Gmail does not
    // alter linear-gradient backgrounds, so we paint the solid color as a
    // gradient in addition to the standard bgcolor/background-color.
    const bgLock = `background-color:${bg};background-image:linear-gradient(${bg},${bg});`;

    // Preserve text color in Gmail dark mode via the blend-mode hack. White
    // text uses screen+difference; dark text uses exclusion+difference.
    const blendText = (inner) =>
      dark
        ? `<div class="gmail-blend-screen"><div class="gmail-blend-difference">${inner}</div></div>`
        : `<div class="gmail-blend-exclusion-blk"><div class="gmail-blend-difference-blk">${inner}</div></div>`;

    const textBlock = blendText(`${disclaimer}${linkRow}${copyright}`);

    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${bg}" style="${bgLock}"><tr><td bgcolor="${bg}" style="padding:${
      p.paddingY
    }px 16px;${bgLock}">${header}${textBlock}</td></tr></table>`;
  },
});

export const blockRegistry = {
  heading: {
    label: "Heading",
    icon: TitleIcon,
    defaultProps: {
      text: "Your headline goes here",
      level: 2,
      align: "left",
      color: "",
      fontFamily: "",
      paddingY: 12,
      paddingX: 24,
    },
    fields: [
      { key: "text", label: "Text", type: "textarea" },
      {
        key: "level",
        label: "Size",
        type: "select",
        options: [
          { value: 1, label: "H1 (large)" },
          { value: 2, label: "H2 (medium)" },
          { value: 3, label: "H3 (small)" },
        ],
      },
      ALIGN,
      FONT_FIELD,
      { key: "color", label: "Text color", type: "color" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
      { key: "paddingX", label: "Padding left/right", type: "number" },
    ],
    toHtml: (p, s) => {
      const size = { 1: 28, 2: 22, 3: 18 }[p.level] || 22;
      const inner = `<span style="font-family:${fontOf(p, s)};font-size:${size}px;font-weight:bold;color:${
        p.color || s.textColor
      };line-height:1.3;">${p.text || ""}</span>`;
      return wrap(inner, { align: p.align, py: p.paddingY, px: p.paddingX });
    },
  },

  text: {
    label: "Text",
    icon: TextFieldsIcon,
    defaultProps: {
      html: "Write your message here. You can include an <a href=\"#\">inline link</a> too.",
      align: "left",
      fontSize: 16,
      color: "",
      fontFamily: "",
      paddingY: 10,
      paddingX: 24,
    },
    fields: [
      { key: "html", label: "Content (HTML allowed)", type: "textarea" },
      ALIGN,
      FONT_FIELD,
      { key: "fontSize", label: "Font size", type: "number" },
      { key: "color", label: "Text color", type: "color" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
      { key: "paddingX", label: "Padding left/right", type: "number" },
    ],
    toHtml: (p, s) => {
      const inner = `<div style="font-family:${fontOf(p, s)};font-size:${
        p.fontSize || 16
      }px;color:${p.color || s.textColor};line-height:1.6;">${p.html || ""}</div>`;
      return wrap(inner, { align: p.align, py: p.paddingY, px: p.paddingX });
    },
  },

  button: {
    label: "Button",
    icon: SmartButtonIcon,
    defaultProps: {
      label: "Click here",
      href: "https://",
      bg: "#069d6e",
      color: "#ffffff",
      radius: 6,
      align: "center",
      fontFamily: "",
      paddingY: 14,
    },
    fields: [
      { key: "label", label: "Button text", type: "text" },
      { key: "href", label: "Link URL", type: "text" },
      ALIGN,
      FONT_FIELD,
      { key: "bg", label: "Background", type: "color" },
      { key: "color", label: "Text color", type: "color" },
      { key: "radius", label: "Corner radius", type: "number" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p, s) => {
      const inner = `<table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><td bgcolor="${
        p.bg
      }" style="border-radius:${p.radius}px;"><a href="${escAttr(
        p.href
      )}" target="_blank" style="display:inline-block;padding:12px 28px;font-family:${fontOf(
        p,
        s
      )};font-size:16px;font-weight:bold;color:${p.color};text-decoration:none;border-radius:${
        p.radius
      }px;">${p.label || ""}</a></td></tr></table>`;
      return wrap(inner, { align: p.align, py: p.paddingY, px: 24 });
    },
  },

  link: {
    label: "Link",
    icon: LinkIcon,
    defaultProps: {
      label: "Read more",
      href: "https://",
      color: "",
      underline: true,
      fontSize: 16,
      fontFamily: "",
      align: "left",
      paddingY: 8,
    },
    fields: [
      { key: "label", label: "Link text", type: "text" },
      { key: "href", label: "Link URL", type: "text" },
      ALIGN,
      FONT_FIELD,
      { key: "color", label: "Color", type: "color" },
      { key: "fontSize", label: "Font size", type: "number" },
      { key: "underline", label: "Underline", type: "checkbox" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p, s) => {
      const inner = `<a href="${escAttr(
        p.href
      )}" target="_blank" style="font-family:${fontOf(p, s)};font-size:${
        p.fontSize || 16
      }px;color:${p.color || s.linkColor};text-decoration:${
        p.underline ? "underline" : "none"
      };">${p.label || ""}</a>`;
      return wrap(inner, { align: p.align, py: p.paddingY, px: 24 });
    },
  },

  image: {
    label: "Image",
    icon: ImageIcon,
    defaultProps: {
      src: "https://via.placeholder.com/600x200?text=Image",
      alt: "",
      href: "",
      width: 600,
      align: "center",
      paddingY: 0,
    },
    fields: [
      { key: "src", label: "Image", type: "image" },
      { key: "alt", label: "Alt text", type: "text" },
      { key: "href", label: "Link URL (optional)", type: "text" },
      { key: "width", label: "Max width (px)", type: "number" },
      ALIGN,
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p, s) => {
      const img = `<img src="${escAttr(p.src)}" alt="${escAttr(
        p.alt
      )}" width="${p.width}" style="display:block;width:100%;max-width:${
        p.width
      }px;height:auto;border:0;outline:none;text-decoration:none;" />`;
      const inner = p.href
        ? `<a href="${escAttr(p.href)}" target="_blank">${img}</a>`
        : img;
      return wrap(inner, { align: p.align, py: p.paddingY, px: 24 });
    },
  },

  divider: {
    label: "Separator",
    icon: HorizontalRuleIcon,
    defaultProps: {
      color: "#e0e0e0",
      thickness: 1,
      style: "solid",
      width: 100,
      paddingY: 12,
    },
    fields: [
      { key: "color", label: "Color", type: "color" },
      { key: "thickness", label: "Thickness (px)", type: "number" },
      {
        key: "style",
        label: "Style",
        type: "select",
        options: [
          { value: "solid", label: "Solid" },
          { value: "dashed", label: "Dashed" },
          { value: "dotted", label: "Dotted" },
        ],
      },
      { key: "width", label: "Width (%)", type: "number" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p) => {
      const inner = `<table role="presentation" width="${
        p.width
      }%" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td style="border-top:${
        p.thickness
      }px ${p.style} ${p.color};font-size:0;line-height:0;">&nbsp;</td></tr></table>`;
      return wrap(inner, { align: "center", py: p.paddingY, px: 24 });
    },
  },

  spacer: {
    label: "Spacer",
    icon: HeightIcon,
    defaultProps: { height: 24 },
    fields: [{ key: "height", label: "Height (px)", type: "number" }],
    toHtml: (p) =>
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="height:${
        p.height
      }px;line-height:${p.height}px;font-size:0;">&nbsp;</td></tr></table>`,
  },

  menu: {
    label: "Navigation",
    icon: MenuIcon,
    defaultProps: {
      items: [
        { label: "Home", href: "#" },
        { label: "About", href: "#" },
        { label: "Contact", href: "#" },
      ],
      orientation: "horizontal",
      separator: "none",
      align: "center",
      color: "",
      fontFamily: "",
      fontSize: 14,
      spacing: 10,
      paddingY: 12,
    },
    fields: [
      {
        key: "items",
        label: "Menu items",
        type: "items",
        itemFields: [
          { key: "label", label: "Label" },
          { key: "href", label: "URL" },
        ],
        newItem: { label: "Link", href: "#" },
      },
      {
        key: "orientation",
        label: "Orientation",
        type: "select",
        options: [
          { value: "horizontal", label: "Horizontal" },
          { value: "vertical", label: "Vertical" },
        ],
      },
      {
        key: "separator",
        label: "Separator",
        type: "select",
        options: [
          { value: "none", label: "None" },
          { value: "pipe", label: "| Pipe" },
          { value: "slash", label: "/ Slash" },
          { value: "dot", label: "• Dot" },
        ],
      },
      ALIGN,
      FONT_FIELD,
      { key: "color", label: "Link color", type: "color" },
      { key: "fontSize", label: "Font size", type: "number" },
      { key: "spacing", label: "Item spacing", type: "number" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p, s) => {
      const vertical = p.orientation === "vertical";
      const sep = SEP_CHARS[p.separator || "none"];
      const links = (p.items || []).map(
        (it) =>
          `<a href="${escAttr(it.href)}" target="_blank" style="${
            vertical ? "display:block;" : "display:inline-block;"
          }color:${p.color || s.linkColor};text-decoration:none;font-family:${fontOf(
            p,
            s
          )};font-size:${p.fontSize || 14}px;padding:${
            vertical ? "6px" : "0"
          } ${p.spacing || 10}px;">${it.label || ""}</a>`
      );
      const joined =
        sep && !vertical
          ? links.join(
              `<span style="color:#cccccc;font-size:${
                p.fontSize || 14
              }px;">${sep}</span>`
            )
          : links.join("");
      return wrap(joined, { align: p.align, py: p.paddingY, px: 24 });
    },
  },

  social: {
    label: "Social",
    icon: ShareIcon,
    defaultProps: {
      links: [
        { network: "Twitter", href: "#" },
        { network: "Instagram", href: "#" },
        { network: "LinkedIn", href: "#" },
      ],
      align: "center",
      fontFamily: "",
      fontSize: 13,
      paddingY: 12,
    },
    fields: [
      {
        key: "links",
        label: "Networks",
        type: "items",
        itemFields: [
          { key: "network", label: "Network" },
          { key: "href", label: "URL" },
        ],
        newItem: { network: "Facebook", href: "#" },
      },
      ALIGN,
      FONT_FIELD,
      { key: "fontSize", label: "Font size", type: "number" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p, s) => {
      const items = (p.links || []).map(
        (it) =>
          `<a href="${escAttr(
            it.href
          )}" target="_blank" style="display:inline-block;margin:0 6px;padding:6px 12px;background-color:#eef1f4;border-radius:4px;color:${
            s.textColor
          };text-decoration:none;font-family:${fontOf(p, s)};font-size:${
            p.fontSize || 13
          }px;">${it.network || ""}</a>`
      );
      return wrap(items.join(""), { align: p.align, py: p.paddingY, px: 24 });
    },
  },

  header: {
    label: "Header / Navbar",
    icon: WebAssetIcon,
    defaultProps: {
      bg: "#0a0a0a",
      logoSrc: TAP_ASSET("logo-dark.png"),
      logoHref: "https://www.tapinvest.com",
      logoWidth: 90,
      logoHeight: 28,
      navItems: [
        { label: "Terminal", href: "#" },
        { label: "Pay", href: "#" },
        { label: "Chat", href: "#" },
        { label: "Invest", href: "#" },
      ],
      navColor: "#ffffff",
      fontFamily: "",
      fontSize: 14,
      ctaLabel: "Explore TAP",
      ctaHref: "#",
      ctaBg: "#069d6e",
      ctaColor: "#ffffff",
      ctaOutline: true,
      paddingY: 18,
    },
    fields: [
      { key: "_logo", label: "Logo", type: "label" },
      { key: "logoSrc", label: "Logo image", type: "image" },
      { key: "logoHref", label: "Logo link", type: "text" },
      { key: "logoWidth", label: "Logo width (px)", type: "number" },
      { key: "logoHeight", label: "Logo height (px)", type: "number" },
      { key: "_nav", label: "Navigation", type: "label" },
      {
        key: "navItems",
        label: "Nav links",
        type: "items",
        itemFields: [
          { key: "label", label: "Label" },
          { key: "href", label: "URL" },
        ],
        newItem: { label: "Link", href: "#" },
      },
      { key: "navColor", label: "Nav link color", type: "color" },
      FONT_FIELD,
      { key: "fontSize", label: "Nav font size", type: "number" },
      { key: "_cta", label: "Call-to-action button", type: "label" },
      { key: "ctaLabel", label: "Button label (blank to hide)", type: "text" },
      { key: "ctaHref", label: "Button URL", type: "text" },
      { key: "ctaBg", label: "Button color", type: "color" },
      { key: "ctaColor", label: "Button text color", type: "color" },
      { key: "ctaOutline", label: "Outlined button", type: "checkbox" },
      { key: "_sec", label: "Section", type: "label" },
      { key: "bg", label: "Background", type: "color" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p, s) => {
      const ff = p.fontFamily || s.fontFamily;
      const logoImg = `<img src="${escAttr(p.logoSrc)}" alt="logo" width="${
        p.logoWidth || 90
      }" height="${
        p.logoHeight || 28
      }" style="display:block;border:0;object-fit:contain;" />`;
      const logo = p.logoHref
        ? `<a href="${escAttr(
            p.logoHref
          )}" style="text-decoration:none;">${logoImg}</a>`
        : logoImg;
      const nav = (p.navItems || [])
        .map(
          (it) =>
            `<a href="${escAttr(
              it.href
            )}" target="_blank" style="display:inline-block;margin:0 9px;font-family:${ff};font-size:${
              p.fontSize || 14
            }px;color:${p.navColor};text-decoration:none;vertical-align:middle;">${
              it.label || ""
            }</a>`
        )
        .join("");
      const cta = p.ctaLabel
        ? `<span style="display:inline-block;vertical-align:middle;margin-left:6px;">${btnHtml(
            {
              label: p.ctaLabel,
              href: p.ctaHref,
              bg: p.ctaBg,
              color: p.ctaColor,
              outline: p.ctaOutline,
              ff,
              size: "sm",
              radius: 8,
            }
          )}</span>`
        : "";
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${
        p.bg
      }" style="${bgLock(p.bg)}"><tr><td style="padding:${
        p.paddingY
      }px 24px;${bgLock(p.bg)}">
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr>
<td align="left" valign="middle" style="vertical-align:middle;">${logo}</td>
<td align="right" valign="middle" style="vertical-align:middle;text-align:right;"><span style="vertical-align:middle;">${nav}</span>${cta}</td>
</tr></table>
</td></tr></table>`;
    },
  },

  buttonRow: {
    label: "Button Row",
    icon: SmartButtonIcon,
    defaultProps: {
      align: "center",
      buttons: [
        { label: "Explore the Updates", href: "#" },
        { label: "Visit TAPInvest.com", href: "#" },
      ],
      bg: "#069d6e",
      color: "#ffffff",
      radius: 8,
      paddingY: 14,
    },
    fields: [
      {
        key: "buttons",
        label: "Buttons (first is filled, rest outlined)",
        type: "items",
        itemFields: [
          { key: "label", label: "Label" },
          { key: "href", label: "URL" },
        ],
        newItem: { label: "Button", href: "#" },
      },
      ALIGN,
      { key: "bg", label: "Button color", type: "color" },
      { key: "color", label: "Text color", type: "color" },
      { key: "radius", label: "Corner radius", type: "number" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p, s) => {
      const ff = s.fontFamily;
      const btns = (p.buttons || [])
        .map((b, i) =>
          btnHtml({
            label: b.label,
            href: b.href,
            bg: p.bg,
            color: p.color,
            radius: p.radius,
            outline: i > 0,
            ff,
          })
        )
        .join("");
      return wrap(`<div style="font-size:0;">${btns}</div>`, {
        align: p.align,
        py: p.paddingY,
        px: 24,
      });
    },
  },

  banner: {
    label: "Banner (BG image)",
    icon: WallpaperIcon,
    defaultProps: {
      bgImage: "",
      bgColor: "#0a0a0a",
      eyebrow: "",
      eyebrowColor: "#069d6e",
      heading: "Explore what's new across TAP.",
      headingColor: "#ffffff",
      text: "New tools. Deeper integration. A stronger ecosystem.",
      textColor: "#c6cad2",
      button1Label: "Visit TAPInvest.com",
      button1Href: "#",
      button2Label: "",
      button2Href: "",
      buttonBg: "#069d6e",
      buttonColor: "#ffffff",
      align: "left",
      paddingY: 48,
      minHeight: 200,
    },
    fields: [
      { key: "bgImage", label: "Background image", type: "image" },
      { key: "bgColor", label: "Background color (fallback)", type: "color" },
      { key: "minHeight", label: "Min height (px)", type: "number" },
      { key: "_content", label: "Content", type: "label" },
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "eyebrowColor", label: "Eyebrow color", type: "color" },
      { key: "heading", label: "Heading", type: "text" },
      { key: "headingColor", label: "Heading color", type: "color" },
      { key: "text", label: "Text", type: "textarea" },
      { key: "textColor", label: "Text color", type: "color" },
      { key: "_btns", label: "Buttons", type: "label" },
      { key: "button1Label", label: "Button 1 label", type: "text" },
      { key: "button1Href", label: "Button 1 URL", type: "text" },
      { key: "button2Label", label: "Button 2 label", type: "text" },
      { key: "button2Href", label: "Button 2 URL", type: "text" },
      { key: "buttonBg", label: "Button color", type: "color" },
      { key: "buttonColor", label: "Button text color", type: "color" },
      ALIGN,
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p, s) => {
      const ff = s.fontFamily;
      const w = s.contentWidth || 600;
      const h = p.minHeight || 200;
      const parts = [];
      if (p.eyebrow)
        parts.push(
          `<div style="font-family:${ff};font-size:12px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:${p.eyebrowColor};margin-bottom:12px;">${p.eyebrow}</div>`
        );
      if (p.heading)
        parts.push(
          `<div style="font-family:${ff};font-size:30px;font-weight:bold;color:${p.headingColor};line-height:1.2;margin-bottom:14px;">${p.heading}</div>`
        );
      if (p.text)
        parts.push(
          `<div style="font-family:${ff};font-size:15px;color:${p.textColor};line-height:1.6;margin-bottom:20px;">${p.text}</div>`
        );
      const b1 = btnHtml({
        label: p.button1Label,
        href: p.button1Href,
        bg: p.buttonBg,
        color: p.buttonColor,
        ff,
      });
      const b2 = btnHtml({
        label: p.button2Label,
        href: p.button2Href,
        bg: p.buttonBg,
        color: p.buttonColor,
        outline: true,
        ff,
      });
      if (b1 || b2)
        parts.push(`<div style="font-size:0;">${b1}${b2}</div>`);
      const content = parts.join("");
      const bgImg = p.bgImage
        ? `background-image:url('${escAttr(
            p.bgImage
          )}');background-size:cover;background-position:center;`
        : "";
      const tdBg = p.bgImage
        ? `background-color:${p.bgColor};${bgImg}`
        : bgLock(p.bgColor);
      const vml = p.bgImage
        ? `<!--[if gte mso 9]>
<v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:${w}px;height:${h}px;">
<v:fill type="frame" src="${escAttr(p.bgImage)}" color="${p.bgColor}" />
<v:textbox inset="0,0,0,0"><![endif]-->`
        : "";
      const vmlEnd = p.bgImage
        ? `<!--[if gte mso 9]></v:textbox></v:rect><![endif]-->`
        : "";
      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${
        p.bgColor
      }" style="background-color:${p.bgColor};"><tr><td background="${
        p.bgImage ? escAttr(p.bgImage) : ""
      }" bgcolor="${p.bgColor}" valign="middle" align="${
        p.align
      }" style="padding:${p.paddingY}px 28px;${tdBg}">
${vml}
<div style="text-align:${p.align};">${content}</div>
${vmlEnd}
</td></tr></table>`;
    },
  },

  split: {
    label: "Split (2 columns)",
    icon: VerticalSplitIcon,
    defaultProps: {
      ratio: "50-50",
      vAlign: "middle",
      gap: 24,
      paddingY: 24,
      leftType: "content",
      leftImage: "",
      leftEyebrow: "Q2 PRODUCT UPDATE",
      leftEyebrowColor: "#069d6e",
      leftHeading: "What TAP Built in Q2",
      leftHeadingColor: "",
      leftText: "This quarter, we continued building the tools that help users discover opportunities, move money, and communicate.",
      leftTextColor: "",
      leftButtonLabel: "Explore the Updates",
      leftButtonHref: "#",
      rightType: "image",
      rightImage: "https://via.placeholder.com/280x280?text=Image",
      rightEyebrow: "",
      rightEyebrowColor: "#069d6e",
      rightHeading: "",
      rightHeadingColor: "",
      rightText: "",
      rightTextColor: "",
      rightButtonLabel: "",
      rightButtonHref: "",
    },
    fields: [
      {
        key: "ratio",
        label: "Column ratio",
        type: "select",
        options: [
          { value: "50-50", label: "50 / 50" },
          { value: "60-40", label: "60 / 40" },
          { value: "40-60", label: "40 / 60" },
        ],
      },
      {
        key: "vAlign",
        label: "Vertical align",
        type: "select",
        options: [
          { value: "top", label: "Top" },
          { value: "middle", label: "Middle" },
        ],
      },
      { key: "gap", label: "Gap (px)", type: "number" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
      { key: "_left", label: "Left column", type: "label" },
      {
        key: "leftType",
        label: "Left type",
        type: "select",
        options: [
          { value: "content", label: "Text content" },
          { value: "image", label: "Image" },
        ],
      },
      { key: "leftImage", label: "Left image", type: "image" },
      { key: "leftEyebrow", label: "Left eyebrow", type: "text" },
      { key: "leftEyebrowColor", label: "Left eyebrow color", type: "color" },
      { key: "leftHeading", label: "Left heading", type: "text" },
      { key: "leftHeadingColor", label: "Left heading color", type: "color" },
      { key: "leftText", label: "Left text", type: "textarea" },
      { key: "leftTextColor", label: "Left text color", type: "color" },
      { key: "leftButtonLabel", label: "Left button label", type: "text" },
      { key: "leftButtonHref", label: "Left button URL", type: "text" },
      { key: "_right", label: "Right column", type: "label" },
      {
        key: "rightType",
        label: "Right type",
        type: "select",
        options: [
          { value: "content", label: "Text content" },
          { value: "image", label: "Image" },
        ],
      },
      { key: "rightImage", label: "Right image", type: "image" },
      { key: "rightEyebrow", label: "Right eyebrow", type: "text" },
      { key: "rightEyebrowColor", label: "Right eyebrow color", type: "color" },
      { key: "rightHeading", label: "Right heading", type: "text" },
      { key: "rightHeadingColor", label: "Right heading color", type: "color" },
      { key: "rightText", label: "Right text", type: "textarea" },
      { key: "rightTextColor", label: "Right text color", type: "color" },
      { key: "rightButtonLabel", label: "Right button label", type: "text" },
      { key: "rightButtonHref", label: "Right button URL", type: "text" },
    ],
    toHtml: (p, s) => {
      const ff = s.fontFamily;
      const ratios = { "50-50": [50, 50], "60-40": [60, 40], "40-60": [40, 60] };
      const [lw, rw] = ratios[p.ratio] || [50, 50];
      const innerWidth = (s.contentWidth || 600) - 48;
      const gap = Number.isFinite(p.gap) ? p.gap : 24;
      const lMax = Math.floor((innerWidth * lw) / 100);
      const rMax = Math.floor((innerWidth * rw) / 100);
      const valign = p.vAlign === "top" ? "top" : "middle";
      const pad = Math.round(gap / 2);

      const side = (prefix, maxW) => {
        const type = p[`${prefix}Type`] || "content";
        if (type === "image") {
          const img = p[`${prefix}Image`];
          return img
            ? `<img src="${escAttr(
                img
              )}" alt="" width="${maxW - gap}" style="display:block;width:100%;max-width:${
                maxW - gap
              }px;height:auto;border:0;border-radius:10px;" />`
            : "";
        }
        const parts = [];
        const eb = p[`${prefix}Eyebrow`];
        const hd = p[`${prefix}Heading`];
        const tx = p[`${prefix}Text`];
        const bl = p[`${prefix}ButtonLabel`];
        if (eb)
          parts.push(
            `<div style="font-family:${ff};font-size:12px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:${
              p[`${prefix}EyebrowColor`] || s.linkColor
            };margin-bottom:10px;">${eb}</div>`
          );
        if (hd)
          parts.push(
            `<div style="font-family:${ff};font-size:26px;font-weight:bold;color:${
              p[`${prefix}HeadingColor`] || s.textColor
            };line-height:1.25;margin-bottom:12px;">${hd}</div>`
          );
        if (tx)
          parts.push(
            `<div style="font-family:${ff};font-size:15px;color:${
              p[`${prefix}TextColor`] || s.textColor
            };line-height:1.6;margin-bottom:16px;">${tx}</div>`
          );
        if (bl)
          parts.push(
            btnHtml({
              label: bl,
              href: p[`${prefix}ButtonHref`],
              bg: s.linkColor,
              ff,
            })
          );
        return parts.join("");
      };

      const col = (prefix, maxW, pctW) =>
        `<!--[if mso]><td width="${pctW}%" valign="${valign}"><![endif]--><div class="ecol" style="width:100%;max-width:${maxW}px;display:inline-block;vertical-align:${valign};text-align:left;font-size:14px;"><div style="padding:0 ${pad}px;">${side(
          prefix,
          maxW
        )}</div></div><!--[if mso]></td><![endif]-->`;

      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="padding:${
        p.paddingY
      }px 24px;"><div style="font-size:0;">
<!--[if mso]><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><![endif]-->
${col("left", lMax, lw)}${col("right", rMax, rw)}
<!--[if mso]></tr></table><![endif]-->
</div></td></tr></table>`;
    },
  },

  columns: {
    label: "Columns / Cards",
    icon: ViewColumnIcon,
    defaultProps: {
      gap: 16,
      paddingY: 16,
      align: "left",
      cardBg: "",
      cardBorder: "",
      cardRadius: 12,
      cardPadding: 16,
      titleColor: "",
      textColor: "",
      columns: [
        {
          icon: "",
          image: "https://via.placeholder.com/300x180?text=Card+1",
          subtitle: "",
          subtitleColor: "#069d6e",
          title: "Card title",
          text: "A short supporting description for this card.",
          linkLabel: "",
          linkHref: "",
          buttonLabel: "",
          buttonHref: "",
        },
        {
          icon: "",
          image: "https://via.placeholder.com/300x180?text=Card+2",
          subtitle: "",
          subtitleColor: "#069d6e",
          title: "Card title",
          text: "A short supporting description for this card.",
          linkLabel: "",
          linkHref: "",
          buttonLabel: "",
          buttonHref: "",
        },
      ],
    },
    fields: [
      { key: "columns", label: "Columns", type: "columns" },
      { key: "align", label: "Text alignment", type: "align" },
      { key: "_cards", label: "Card styling (optional)", type: "label" },
      { key: "cardBg", label: "Card background", type: "color" },
      { key: "cardBorder", label: "Card border color", type: "color" },
      { key: "cardRadius", label: "Card corner radius", type: "number" },
      { key: "cardPadding", label: "Card inner padding", type: "number" },
      { key: "titleColor", label: "Title color", type: "color" },
      { key: "textColor", label: "Text color", type: "color" },
      { key: "_layout", label: "Layout", type: "label" },
      { key: "gap", label: "Gap between columns (px)", type: "number" },
      { key: "paddingY", label: "Padding top/bottom", type: "number" },
    ],
    toHtml: (p, s) => {
      const cols = p.columns && p.columns.length ? p.columns : [{}];
      const n = cols.length;
      const gap = Number.isFinite(p.gap) ? p.gap : 16;
      const ff = s.fontFamily;
      const align = p.align || "left";
      const titleColor = p.titleColor || s.textColor;
      const textColor = p.textColor || s.textColor;
      const innerWidth = (s.contentWidth || 600) - 48;
      const colMax = Math.max(120, Math.floor(innerWidth / n));
      const pad = Math.round(gap / 2);
      const pctWidth = (100 / n).toFixed(4);
      const hasCard = !!(p.cardBg || p.cardBorder);
      const cardPad = Number.isFinite(p.cardPadding) ? p.cardPadding : 16;
      const imgW = colMax - gap - (hasCard ? cardPad * 2 : 0);

      const renderCol = (c) => {
        const parts = [];
        if (c.icon) {
          parts.push(
            `<img src="${escAttr(c.icon)}" alt="" width="40" style="display:${
              align === "center" ? "inline-block" : "block"
            };width:40px;height:40px;object-fit:contain;border:0;margin:0 0 12px 0;" />`
          );
        }
        if (c.image) {
          parts.push(
            `<img src="${escAttr(c.image)}" alt="${escAttr(
              c.title || ""
            )}" width="${imgW}" style="display:block;width:100%;max-width:${imgW}px;height:auto;border:0;border-radius:8px;margin:0 0 14px 0;" />`
          );
        }
        if (c.subtitle) {
          parts.push(
            `<div style="font-family:${ff};font-size:13px;font-weight:600;color:${
              c.subtitleColor || s.linkColor
            };line-height:1.4;margin-bottom:6px;">${c.subtitle}</div>`
          );
        }
        if (c.title) {
          parts.push(
            `<div style="font-family:${ff};font-size:18px;font-weight:bold;color:${titleColor};line-height:1.3;margin-bottom:8px;">${c.title}</div>`
          );
        }
        if (c.text) {
          parts.push(
            `<div style="font-family:${ff};font-size:14px;color:${textColor};line-height:1.6;margin-bottom:12px;">${c.text}</div>`
          );
        }
        if (c.linkLabel) {
          parts.push(
            `<a href="${escAttr(
              c.linkHref || "#"
            )}" target="_blank" style="font-family:${ff};font-size:14px;font-weight:600;color:${
              s.linkColor
            };text-decoration:none;">${c.linkLabel} &rarr;</a>`
          );
        }
        if (c.buttonLabel) {
          parts.push(
            btnHtml({
              label: c.buttonLabel,
              href: c.buttonHref,
              bg: s.linkColor,
              ff,
              size: "sm",
            })
          );
        }
        const inner = parts.join("");
        if (!hasCard) return inner;
        return `<div style="${bgLock(p.cardBg)}${
          p.cardBorder ? `border:1px solid ${p.cardBorder};` : ""
        }border-radius:${p.cardRadius || 12}px;padding:${cardPad}px;">${inner}</div>`;
      };

      const msoTdOpen = `<!--[if mso]><td width="${pctWidth}%" valign="top"><![endif]-->`;
      const msoTdClose = `<!--[if mso]></td><![endif]-->`;

      const columnsHtml = cols
        .map(
          (c) =>
            `${msoTdOpen}<div class="ecol" style="width:100%;max-width:${colMax}px;display:inline-block;vertical-align:top;text-align:${align};font-size:14px;"><div style="padding:${pad}px;">${renderCol(
              c
            )}</div></div>${msoTdClose}`
        )
        .join("");

      return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="padding:${p.paddingY}px 24px;" align="${align}">
<div style="font-size:0;text-align:${align};">
<!--[if mso]><table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><![endif]-->
${columnsHtml}
<!--[if mso]></tr></table><![endif]-->
</div>
</td></tr></table>`;
    },
  },

  footer: footerDef("light"),
  footerDark: footerDef("dark"),

  html: {
    label: "Custom HTML",
    icon: CodeIcon,
    defaultProps: {
      code: "<p style=\"text-align:center;font-family:Arial;\">Custom HTML here</p>",
    },
    fields: [{ key: "code", label: "HTML code", type: "textarea" }],
    toHtml: (p) =>
      `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0"><tr><td style="padding:0 24px;">${
        p.code || ""
      }</td></tr></table>`,
  },
};

export const blockOrder = [
  "header",
  "heading",
  "text",
  "button",
  "buttonRow",
  "link",
  "image",
  "banner",
  "split",
  "columns",
  "divider",
  "spacer",
  "menu",
  "social",
  "footer",
  "footerDark",
  "html",
];
